import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, ArrowUpRight } from "lucide-react";
import { PageHeader, Reveal, useSeo } from "@/components/site/Motion";
import { journals, information, site, OJS_BASE } from "@/content/site";

const index = [
  ...journals.flatMap((j) => [
    { title: j.title, kind: "Journal", text: j.about.join(" "), to: `/index.php/${j.slug}` },
    { title: `${j.abbr} · Submissions`, kind: "Page", text: site.checklist.join(" "), to: `/index.php/${j.slug}/about/submissions` },
    { title: `${j.abbr} · Contact`, kind: "Page", text: "contact editorial office", to: `/index.php/${j.slug}/about/contact` },
  ]),
  ...site.ourJournals.map((d) => ({ title: d.name, kind: "Discipline", text: d.short, to: "/#journals" })),
  ...Object.entries(information).map(([k, v]) => ({ title: v.title, kind: "Information", text: v.body, to: `/index.php/ESGS/information/${k}` })),
  { title: "Privacy Statement", kind: "Page", text: site.privacy, to: "/index.php/ESGS/about/privacy" },
];

export default function Search() {
  useSeo("Search", "Search Motive Press journals and pages.");
  const [q, setQ] = useState("");
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return index;
    return index.filter((r) => `${r.title} ${r.text}`.toLowerCase().includes(s));
  }, [q]);

  return (
    <div data-testid="search-page">
      <PageHeader eyebrow="Search" title="Find a journal, page or article." />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-28">
        <div className="flex items-center gap-4 border-b-2 border-[var(--ink)] pb-3 mb-4">
          <SearchIcon className="w-7 h-7 shrink-0" />
          <label htmlFor="q" className="sr-only">Search</label>
          <input id="q" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a keyword…" className="flex-1 bg-transparent outline-none font-display text-3xl sm:text-5xl placeholder:text-[#9A958C]" data-testid="search-input" />
        </div>
        <div className="flex flex-wrap justify-between gap-4 text-sm text-[var(--ink-muted)] mb-10">
          <span data-testid="search-count">{results.length} result{results.length === 1 ? "" : "s"}</span>
          <a href={`${OJS_BASE}/index/search/search?query=${encodeURIComponent(q)}`} className="link-u text-[var(--emerald)] inline-flex items-center gap-1" data-testid="search-ojs-articles">Search published articles in OJS <ArrowUpRight className="w-4 h-4" /></a>
        </div>
        <ul className="border-t border-[var(--line)]">
          {results.map((r, i) => (
            <Reveal as="li" key={r.to + r.title} delay={Math.min(i, 6) * 0.03}>
              <Link to={r.to} className="group grid grid-cols-[1fr_auto] sm:grid-cols-[140px_1fr_auto] gap-x-6 gap-y-1 items-center py-6 border-b border-[var(--line)] hover:bg-white transition-colors px-2" data-testid={`search-result-${i}`}>
                <span className="font-mono-x text-xs tracking-[0.18em] uppercase text-[var(--terracotta)] col-span-2 sm:col-span-1">{r.kind}</span>
                <span className="font-display text-2xl sm:text-3xl">{r.title}</span>
                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:rotate-45" />
              </Link>
            </Reveal>
          ))}
        </ul>
        {results.length === 0 && <p className="py-16 font-display text-3xl text-[var(--ink-muted)]" data-testid="search-empty">No matches. Try another keyword or search published articles in OJS.</p>}
      </div>
    </div>
  );
}
